import axios from "axios";
import { store } from "../redux/store";

export const baseURL = process.env.REACT_APP_BASE_URL;

// Browser-compatible connection optimization
// Modern browsers handle HTTP/2 multiplexing and connection pooling automatically
// Our optimizations focus on request queuing, caching, and prioritization

// Token cache to avoid Redux store access on every request
let cachedToken = null;
let tokenExpiry = null;
const TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Request queue management for concurrent requests
class RequestQueue {
  constructor() {
    this.queue = [];
    this.activeRequests = 0;
    // this.maxConcurrent = 8; // Browser-friendly limit
    this.maxConcurrent = 2; // Browser-friendly limit
    this.priorities = {
      high: [], // User actions, mutations
      normal: [], // Regular data fetching
      low: [], // Background/prefetch requests
    };
  }

  async execute(requestFn, priority = "normal") {
    return new Promise((resolve, reject) => {
      const request = {
        fn: requestFn,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      // Add to appropriate priority queue
      this.priorities[priority].push(request);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.activeRequests >= this.maxConcurrent) {
      return; // Max concurrent requests reached
    }

    // Get next request by priority (high -> normal -> low)
    const request = this.getNextRequest();
    if (!request) {
      return; // No requests to process
    }

    this.activeRequests++;

    try {
      const result = await request.fn();
      request.resolve(result);
    } catch (error) {
      request.reject(error);
    } finally {
      this.activeRequests--;
      // Process next request in queue
      setTimeout(() => this.processQueue(), 0);
    }
  }

  getNextRequest() {
    // Check high priority first
    if (this.priorities.high.length > 0) {
      return this.priorities.high.shift();
    }
    // Then normal priority
    if (this.priorities.normal.length > 0) {
      return this.priorities.normal.shift();
    }
    // Finally low priority
    if (this.priorities.low.length > 0) {
      return this.priorities.low.shift();
    }
    return null;
  }

  // Method to clear old requests (prevent memory leaks)
  clearStaleRequests() {
    const staleThreshold = Date.now() - 30000; // 30 seconds
    Object.keys(this.priorities).forEach((priority) => {
      this.priorities[priority] = this.priorities[priority].filter(
        (request) => request.timestamp > staleThreshold
      );
    });
  }
}

const requestQueue = new RequestQueue();

// Clear stale requests every minute
setInterval(() => requestQueue.clearStaleRequests(), 60000);

const axiosInstances = axios.create({
  baseURL: baseURL,
  timeout: 15000, // 15 second timeout
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    // Removed unsafe headers that browsers manage automatically:
    // - Connection: browsers handle connection pooling and keep-alive
    // - Accept-Encoding: browsers handle compression negotiation
  },
  // Enable connection pooling and keep-alive
  maxRedirects: 5,
  maxContentLength: 50 * 1024 * 1024, // 50MB max response size
  decompress: true, // Automatically decompress gzipped responses

  // Browser-specific optimizations
  // Enable HTTP/2 multiplexing (browser handles this automatically)
  // Connection pooling is handled by the browser's network layer
});

const axiosInstancesImage = axios.create({
  baseURL: baseURL,
  timeout: 30000, // 30 seconds for image uploads
  maxContentLength: 100 * 1024 * 1024, // 100MB for images
});

// Separate request handler for multipart/form-data uploads
const onRequestMultipart = (config) => {
  const accessToken = getCachedToken();

  if (accessToken) {
    config.headers = {
      Accept: "application/json",
      "Content-Type": "multipart/form-data",
      Authorization: "JWT " + accessToken,
    };
  }

  // Remove forbidden headers (same as main interceptor)
  const forbiddenHeaders = [
    "accept-charset",
    "accept-encoding",
    "access-control-request-headers",
    "access-control-request-method",
    "connection",
    "content-length",
    "cookie",
    "cookie2",
    "date",
    "dnt",
    "expect",
    "host",
    "keep-alive",
    "origin",
    "referer",
    "te",
    "trailer",
    "transfer-encoding",
    "upgrade",
    "via",
    "user-agent",
  ];

  forbiddenHeaders.forEach((header) => {
    const lowerHeader = header.toLowerCase();
    const upperHeader = header.toUpperCase();
    const titleHeader = header.charAt(0).toUpperCase() + header.slice(1).toLowerCase();

    [lowerHeader, upperHeader, titleHeader, header].forEach((variant) => {
      if (config.headers && config.headers[variant]) {
        delete config.headers[variant];
        console.debug(`[Interceptor Multipart] Removed forbidden header: ${variant}`);
      }
    });
  });

  return config;
};

const axiosInstancesZIP = axios.create({
  baseURL: baseURL,
  timeout: 60000, // 60 seconds for ZIP downloads
  responseType: "blob", // Important for file downloads
  headers: {
    Accept: "*/*",
    "Content-Type": "*/*",
  },
  maxContentLength: 500 * 1024 * 1024, // 500MB for ZIP files
});

// Cached token retrieval to avoid Redux store access on every request
const getCachedToken = () => {
  const now = Date.now();

  // If token is cached and not expired, return it
  if (cachedToken && tokenExpiry && now < tokenExpiry) {
    return cachedToken;
  }

  // Otherwise, get fresh token from store and cache it
  try {
    const states = store.getState();
    cachedToken = states.auth?.authToken || null;
    tokenExpiry = now + TOKEN_CACHE_DURATION;
    return cachedToken;
  } catch (error) {
    console.warn("Error accessing Redux store for token:", error);
    return null;
  }
};

// Function to clear token cache (call this on logout or token refresh)
const clearTokenCache = () => {
  cachedToken = null;
  tokenExpiry = null;
};

const onRequest = (config) => {
  const accessToken = getCachedToken();

  if (accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: "JWT " + accessToken,
    };
  }

  // Browser safety: Remove any forbidden headers that might have been added
  // These headers are managed by the browser and cannot be set via JavaScript
  const forbiddenHeaders = [
    "accept-charset",
    "accept-encoding",
    "access-control-request-headers",
    "access-control-request-method",
    "connection",
    "content-length",
    "cookie",
    "cookie2",
    "date",
    "dnt",
    "expect",
    "host",
    "keep-alive",
    "origin",
    "referer",
    "te",
    "trailer",
    "transfer-encoding",
    "upgrade",
    "via",
    "user-agent",
  ];

  forbiddenHeaders.forEach((header) => {
    const lowerHeader = header.toLowerCase();
    const upperHeader = header.toUpperCase();
    const titleHeader = header.charAt(0).toUpperCase() + header.slice(1).toLowerCase();

    [lowerHeader, upperHeader, titleHeader, header].forEach((variant) => {
      if (config.headers && config.headers[variant]) {
        delete config.headers[variant];
        console.debug(`[Interceptor] Removed forbidden header: ${variant}`);
      }
    });
  });

  return config;
};

const onRequestZIP = (config) => {
  const accessToken = getCachedToken();

  if (accessToken) {
    config.headers = {
      ...config.headers,
      Accept: "*/*",
      "Content-Type": "*/*",
      Authorization: "JWT " + accessToken,
    };
  }

  // Remove forbidden headers (same as other interceptors)
  const forbiddenHeaders = [
    "accept-charset",
    "accept-encoding",
    "access-control-request-headers",
    "access-control-request-method",
    "connection",
    "content-length",
    "cookie",
    "cookie2",
    "date",
    "dnt",
    "expect",
    "host",
    "keep-alive",
    "origin",
    "referer",
    "te",
    "trailer",
    "transfer-encoding",
    "upgrade",
    "via",
    "user-agent",
  ];

  forbiddenHeaders.forEach((header) => {
    const lowerHeader = header.toLowerCase();
    const upperHeader = header.toUpperCase();
    const titleHeader = header.charAt(0).toUpperCase() + header.slice(1).toLowerCase();

    [lowerHeader, upperHeader, titleHeader, header].forEach((variant) => {
      if (config.headers && config.headers[variant]) {
        delete config.headers[variant];
        console.debug(`[Interceptor ZIP] Removed forbidden header: ${variant}`);
      }
    });
  });

  return config;
};

export const onRequestError = (error) => {
  const customNavigate = useCustomNavigate();
  if (error && error.request && error.request.status === 401) {
    const requestUrl = error.request.responseURL;
    const refreshExp = localStorage.getItem("refreshExp");
    if (refreshExp) {
      if (new Date(refreshExp * 1000).getTime() < new Date().getTime()) {
        customNavigate("/login");
      }
    }
  }

  return Promise.reject(error);
};

const onResponse = (response) => {
  return response;
};

function useCustomNavigate() {
  const customNavigate = (path) => {
    localStorage.setItem("previousPath", window.location.pathname);
    localStorage.setItem("previousSearch", window.location.search);
    window.location.href = path;
  };
  return customNavigate;
}

function onResponseError(error) {
  const customNavigate = useCustomNavigate();
  // const navigate = useNavigate();
  // const { isLoading: loadingRefresh, mutateAsync: refreshMutate } = useMutation(refresh, {
  //   onSuccess: async (data) => {
  //     const accessToken = data.access;
  //     // const decoded = jwt_decode(accessToken);
  //     // await dispatch(actions.login(accessToken));
  //     // console.log("TOKEN", accessToken);
  //     const decoded = jwt_decode(accessToken);
  //     await dispatch(actions.login(accessToken));
  //     await dispatch(actions.refreshToken(auth.refreshToken));
  //     await dispatch(actions.userLoaded(decoded));
  //     // console.log("local", localStorage);
  //     // localStorage.setItem(
  //     //   "persist:root",
  //     //   JSON.stringify({
  //     //     auth: { authToken: accessToken, refreshToken: auth.refreshToken, user: auth.user },
  //     //   })
  //     // );
  //     // console.log("local", localStorage);
  //     // await dispatch(actions.userLoaded(decode));
  //   },
  // });
  // handling the response of 401 unauthorized or signature expired
  // const { auth } = useSelector((state) => state);
  // console.log(auth);
  // TODO: if request URL was not /token/refresh, get new token and refetch URL
  if (error && error.response && error.response.status === 401) {
    const backendUrl = error.request.responseURL;
    const pathname =
      backendUrl.split("//")[1].split("/")[1] + "/" + backendUrl.split("//")[1].split("/")[2];
    if (pathname == "token/refresh") {
      customNavigate("/login");
    } else {
      // TODO: check whether refresh token is expired by only passing the expiry date from the refresh token to the frontend
      // console.log(localStorage.getItem("loadingRefresh"));
      const refreshExp = localStorage.getItem("refreshExp");
      if (refreshExp) {
        if (new Date(refreshExp * 1000).getTime() < new Date().getTime()) {
          customNavigate("/login");
        }
      }
      console.log("ERROR for ", pathname);
    }
  }
  return Promise.reject(error);
}

function setupInterceptorsTo(axiosInstances) {
  axiosInstances.interceptors.request.use(onRequest, onRequestError);
  axiosInstances.interceptors.response.use(onResponse, onResponseError);
  return axiosInstances;
}
const axiosInstance = setupInterceptorsTo(axiosInstances);

function setupInterceptorsZIPTo(axiosInstances) {
  axiosInstancesZIP.interceptors.request.use(onRequestZIP, onRequestError);
  axiosInstancesZIP.interceptors.response.use(onResponse, onResponseError);
  return axiosInstances;
}

function setupInterceptorsImageTo(axiosInstances) {
  axiosInstancesImage.interceptors.request.use(onRequestMultipart, onRequestError);
  axiosInstancesImage.interceptors.response.use(onResponse, onResponseError);
  return axiosInstances;
}

// Wrapper function for queued requests
export const queuedRequest = (axiosCall, priority = "normal") => {
  return requestQueue.execute(axiosCall, priority);
};

// Enhanced axios instances with automatic queuing
const createQueuedInstance = (instance) => {
  const queuedInstance = (...args) => {
    return queuedRequest(() => instance(...args), "normal");
  };
  Object.assign(queuedInstance, instance);

  // Wrap HTTP methods with queuing
  ["get", "post", "put", "patch", "delete"].forEach((method) => {
    const originalMethod = instance[method];
    queuedInstance[method] = (...args) => {
      // Determine priority based on HTTP method
      let priority = "normal";
      if (["post", "put", "patch", "delete"].includes(method)) {
        priority = "high"; // User actions get high priority
      }

      return queuedRequest(() => originalMethod.apply(instance, args), priority);
    };
  });

  return queuedInstance;
};

export const axiosInstanceZIP = setupInterceptorsZIPTo(axiosInstancesZIP);
export const axiosInstanceImage = setupInterceptorsImageTo(axiosInstancesImage);

// Export both regular and queued versions
export const axiosInstanceQueued = createQueuedInstance(axiosInstance);
export const axiosInstanceImageQueued = createQueuedInstance(axiosInstanceImage);

export { clearTokenCache, requestQueue }; // Export token cache clearing function and queue
export default axiosInstanceQueued;
